// cohorts/create hooks
// TanStack Query and client-state hooks only. Pure API functions belong in services/.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { STALE_TIME } from "@core/libs/react-query";
import { toast } from "@core/toast";
import { cohortKeys } from "../../_shared/keys";
import { createCohort, listAdministrators } from "../services";
import type { CreateCohortFormValues } from "../types";

/** The Administrators who can be named responsible. Asked for only while the dialog is open. */
export function useAdministrators() {
  return useQuery({
    queryKey: cohortKeys.administrators(),
    queryFn: listAdministrators,
    staleTime: STALE_TIME.operational,
  });
}

/**
 * Creates a cohort. On success the list is reloaded, the new cohort is put in the detail cache (so
 * its page opens without a second request) and the toast says so.
 */
export function useCreateCohort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      values: CreateCohortFormValues;
      idempotencyKey: string;
    }) => createCohort(input.values, input.idempotencyKey),
    onSuccess: (cohort) => {
      queryClient.setQueryData(cohortKeys.detail(cohort.id), cohort);
      void queryClient.invalidateQueries({ queryKey: cohortKeys.list() });
      toast.success(
        { key: "cohorts:create.created.title" },
        {
          key: "cohorts:create.created.description",
          vars: { name: cohort.name },
        }
      );
    },
  });
}
